import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";

let enRegistered = false;

const ensureEnglishLocale = () => {
  if (!enRegistered) {
    countries.registerLocale(enLocale);
    enRegistered = true;
  }
};

const nameAliases = {
  "czech republic": "CZ",
  "south korea": "KR",
  "north korea": "KP",
  russia: "RU",
  "united states": "US",
  "united kingdom": "GB",
  vietnam: "VN",
  "ivory coast": "CI",
  "myanmar (burma)": "MM",
};

export const getAlpha2FromEnglishCountryName = (countryName) => {
  if (!countryName || typeof countryName !== "string") {
    return null;
  }

  const trimmed = countryName.trim();
  if (!trimmed) {
    return null;
  }

  ensureEnglishLocale();

  const alias = nameAliases[trimmed.toLowerCase()];
  if (alias) {
    return alias;
  }

  const fromLib = countries.getAlpha2Code(trimmed, "en");
  if (fromLib) {
    return fromLib;
  }

  return null;
};

const ROUTE_ALPHA2_TO_BUCKET_KEY = {
  EE: "Estonia",
  LV: "Latvia",
  LT: "Lithuania",
  RU: "Russia",
  PL: "Poland",
  FI: "Finland",
};

const DEFAULT_ROUTE_COUNTRY_ORDER = Object.values(ROUTE_ALPHA2_TO_BUCKET_KEY);

const LOCALE_PRIORITY_COUNTRY = {
  lt: "Lithuania",
  lv: "Latvia",
  pl: "Poland",
  fi: "Finland",
};

const ISO_LANG_PROBE_ORDER = ["en", "et", "lv", "lt", "ru", "fi", "pl"];

const registeredIsoLocales = new Set();

const ensureIsoCountriesLocaleRegistered = (lang) => {
  if (!lang || registeredIsoLocales.has(lang)) {
    return;
  }
  try {
    countries.registerLocale(require(`i18n-iso-countries/langs/${lang}.json`));
    registeredIsoLocales.add(lang);
  } catch {}
};

export const resolveRouteCountryBucketKey = (countryName, preferredLang) => {
  if (!countryName || typeof countryName !== "string") {
    return null;
  }

  const trimmed = countryName.trim();
  if (!trimmed) {
    return null;
  }

  ensureEnglishLocale();

  const probeOrder = [
    preferredLang,
    ...ISO_LANG_PROBE_ORDER.filter((l) => l !== preferredLang),
  ].filter(Boolean);

  let alpha2 = null;
  for (const lang of [...new Set(probeOrder)]) {
    ensureIsoCountriesLocaleRegistered(lang);
    const code = countries.getAlpha2Code(trimmed, lang);
    if (code) {
      alpha2 = code;
      break;
    }
  }

  if (!alpha2) {
    alpha2 = getAlpha2FromEnglishCountryName(trimmed);
  }

  if (!alpha2) {
    return null;
  }

  return ROUTE_ALPHA2_TO_BUCKET_KEY[alpha2] ?? null;
};

const getRouteCountrySortKey = (countryName, locale) => {
  const bucketKey =
    resolveRouteCountryBucketKey(countryName, locale) || countryName;
  const priorityCountry = LOCALE_PRIORITY_COUNTRY[locale];

  if (priorityCountry && bucketKey === priorityCountry) {
    return -1;
  }

  const defaultIdx = DEFAULT_ROUTE_COUNTRY_ORDER.indexOf(bucketKey);

  return defaultIdx === -1 ? DEFAULT_ROUTE_COUNTRY_ORDER.length : defaultIdx;
};

export const sortRoutesGroupedByLocale = (countries, locale) => {
  if (!countries?.length) {
    return countries;
  }

  return [...countries].sort(
    (a, b) =>
      getRouteCountrySortKey(a.countryName, locale) -
      getRouteCountrySortKey(b.countryName, locale),
  );
};

export const sortCountryEntriesByLocale = (entries, locale) => {
  if (!entries?.length) {
    return entries;
  }

  return [...entries].sort(
    ([countryNameA], [countryNameB]) =>
      getRouteCountrySortKey(countryNameA, locale) -
      getRouteCountrySortKey(countryNameB, locale),
  );
};

const LOCALE_DESTINATION_COUNTRY_ORDER = {
  et: ["Estonia", "Latvia", "Lithuania", "Poland", "Finland", "Russia"],
  lv: ["Latvia", "Estonia", "Lithuania", "Poland", "Finland", "Russia"],
  lt: ["Latvia", "Poland", "Estonia", "Lithuania", "Finland", "Russia"],
  pl: ["Lithuania", "Latvia", "Estonia", "Poland", "Finland", "Russia"],
  fi: ["Finland", "Estonia", "Latvia", "Lithuania", "Poland", "Russia"],
};

const DEFAULT_DESTINATION_COUNTRY_ORDER = LOCALE_DESTINATION_COUNTRY_ORDER.et;

const getDestinationCountrySortKey = (countryName, locale) => {
  const order =
    LOCALE_DESTINATION_COUNTRY_ORDER[locale] ||
    DEFAULT_DESTINATION_COUNTRY_ORDER;
  const idx = order.indexOf(countryName);

  return idx === -1 ? order.length : idx;
};

export const sortDestinationCountryEntriesByLocale = (entries, locale) => {
  if (!entries?.length) {
    return entries;
  }

  return [...entries].sort(
    ([countryNameA], [countryNameB]) =>
      getDestinationCountrySortKey(countryNameA, locale) -
      getDestinationCountrySortKey(countryNameB, locale),
  );
};
